import {
    MutationFunction,
    useMutation,
    UseMutationOptions,
    useQuery,
    UseQueryOptions,
} from '@tanstack/react-query';
import { useContext } from 'react';
import { AppContext } from '../sporttia/AppContext';
import { useAPI } from '../lib/hooks';
import {
    GetGame200,
    PostGamePlayer200,
    PostGamePlayerParams,
    PostGameSignUpParams,
    PostSignup200,
} from '../types/api/paths/Game';

const resource = '/games';

const getGetGameQueryKey = (id: number) => [`${resource}/${id}`];

export default function useGameService() {
    const { migrationApis } = useContext(AppContext)!;
    const [request] = useAPI(migrationApis);

    /**
     * GET /games/{id} request.
     */
    function useGetGame<TData = GetGame200>(
        id: number,
        queryOptions?: UseQueryOptions<GetGame200, unknown, TData>,
    ) {
        const queryKey = queryOptions?.queryKey ?? getGetGameQueryKey(id);

        const queryFn = () => request<GetGame200>('GET', `${resource}/${id}`);

        const query = useQuery({ queryKey, queryFn, ...queryOptions });

        return {
            ...query,
            queryKey,
        };
    }

    /**
     * POST /games/{id}/signup request.
     */
    function usePostGameSignUp(
        id: number,
        mutationOptions?: UseMutationOptions<
            PostSignup200,
            unknown,
            { params?: PostGameSignUpParams }
        >,
    ) {
        const mutationFn: MutationFunction<
            PostSignup200,
            { params: PostGameSignUpParams }
        > = ({ params }: { params: PostGameSignUpParams }) =>
            request('POST', `${resource}/${id}/signup`, params);

        return useMutation({ mutationFn, ...mutationOptions });
    }

    /**
     * POST /games/{id}/players request.
     */
    function usePostGamePlayer(
        id: number,
        mutationOptions?: UseMutationOptions<
            PostGamePlayer200,
            unknown,
            { params?: PostGamePlayerParams }
        >,
    ) {
        const mutationFn: MutationFunction<
            PostGamePlayer200,
            { params: PostGamePlayerParams }
        > = ({ params }: { params: PostGamePlayerParams }) =>
            request('POST', `${resource}/${id}/players`, params);

        return useMutation({ mutationFn, ...mutationOptions });
    }

    function useDeleteGamePlayers(
        mutationOptions?: UseMutationOptions<
            unknown,
            unknown,
            { id: number; params?: unknown }
        >,
    ) {
        const mutationFn: MutationFunction<
            unknown,
            { id: number; params?: unknown }
        > = ({ id, params }: any) =>
            request(
                'DELETE',
                `${resource}/players/${id}`,
                params as Record<string, unknown> | undefined,
            );

        return useMutation({ mutationFn, ...mutationOptions });
    }

    /**
     * DELETE /games/{id} request.
     *
     * Cancela un partido. `params.paymentAction` (DELETING_PAYMENT_ACTION) indica
     * qué hacer con los pagos asociados; el backend lo delega en deleteBooking.
     */
    function useDeleteGame(
        mutationOptions?: UseMutationOptions<
            unknown,
            unknown,
            { id: number; params?: { paymentAction?: number } }
        >,
    ) {
        const mutationFn: MutationFunction<
            unknown,
            { id: number; params?: { paymentAction?: number } }
        > = ({ id, params }) =>
            request(
                'DELETE',
                `${resource}/${id}`,
                params as Record<string, unknown> | undefined,
            );

        return useMutation({ mutationFn, ...mutationOptions });
    }

    return {
        useGetGame,
        usePostGameSignUp,
        usePostGamePlayer,
        useDeleteGamePlayers,
        useDeleteGame,
    };
}
